export class User {
  id: string | null;
  postgresId: string | null;
  email: string | null;
  name: string | null;
  profileImgUrl: string | null;
  defaultWorkspaceId: string | null;
  mergedFlags: Array<string>;
  mergedConfig: any;
  tenantId: string | null;
  appConsentGiven: boolean;

  constructor() {
    this.id = null;
    this.postgresId = null;
    this.name = null;
    this.email = null;
    this.profileImgUrl = null;
    this.defaultWorkspaceId = null;
    this.mergedFlags = [];
    this.mergedConfig = {};
    this.tenantId = null;
    this.appConsentGiven = false;
  }

  setId(id: string | null): void {
    this.id = id;
  }

  setPostgresId(id: string): void {
    this.postgresId = id;
  }

  setEmail(email: string | null): void {
    this.email = email;
  }

  setName(name: string | null): void {
    this.name = name;
  }

  setProfileImgUrl(url: string | null): void {
    this.profileImgUrl = url;
  }

  setDefaultWorkspaceId(id: string | null): void {
    this.defaultWorkspaceId = id;
  }

  setMergedFlags(mergedFlags: Array<string>): void {
    this.mergedFlags = mergedFlags;
  }

  setMergedConfig(mergedConfig: any): void {
    this.mergedConfig = mergedConfig;
  }

  setTenantId(tenantId: string): void {
    this.tenantId = tenantId;
  }

  setAppConsentGiven(appConsentGivenAt: string | null): void {
    this.appConsentGiven = appConsentGivenAt !== null;
  }

  hasDocumentSpaces(): boolean {
    return this.mergedFlags && this.mergedFlags.includes("shared_spaces");
  }

  canUseSharedSpaces(): boolean {
    return !this.mergedFlags?.includes("disable_shared_spaces");
  }

  hasDocumentSpaceLinks(): boolean {
    return (
      this.mergedFlags &&
      this.mergedFlags.includes("shared_spaces_custom_links")
    );
  }

  hasAnalytics(): boolean {
    return this.mergedFlags && this.mergedFlags.includes("analytics");
  }

  hasDocumentAnalytics(): boolean {
    return this.mergedFlags && this.mergedFlags.includes("document_analytics");
  }

  hasPresentationAnalyticsDashboards(): boolean {
    return (
      this.mergedFlags &&
      this.mergedFlags.includes("presentation_analytics_dashboards")
    );
  }

  hasPresentationAnalytics(): boolean {
    return (
      this.hasPresentationAnalyticsDashboards() && this.hasDocumentTracking()
    );
  }

  hasUsageAnalytics(): boolean {
    return this.mergedFlags && this.mergedFlags.includes("usage_analytics");
  }

  hasAllTimeMembershipUsageAnalytics(): boolean {
    return (
      this.mergedFlags &&
      this.mergedFlags.includes("usage_analytics_all_time_membership")
    );
  }

  hasCRMIntegration(): boolean {
    return this.mergedFlags && this.mergedFlags.includes("crm_integration");
  }

  canCreateWorkspace(): boolean {
    return (
      this.mergedFlags && this.mergedFlags.includes("can_create_workspace")
    );
  }

  canDisableSpaceTracking(): boolean {
    return (
      this.mergedFlags &&
      this.mergedFlags.includes("can_disable_space_tracking")
    );
  }

  hideNewUsersColumnInUsageAnalytics(): boolean {
    return (
      this.mergedFlags &&
      this.mergedFlags.includes("hide_new_users_columns_in_usage_analytics")
    );
  }

  hasCustomFlows(): boolean {
    return this.mergedFlags && this.mergedFlags.includes("custom_flows");
  }

  canShareCustomFlowsInSharedSpaces(): boolean {
    return (
      this.mergedFlags &&
      this.mergedFlags.includes("share_custom_flows_in_shared_spaces")
    );
  }

  canSendSpaceMail(): boolean {
    return this.mergedFlags && this.mergedFlags.includes("space_mail");
  }

  hasSignConsent(): boolean {
    return this.mergedFlags && this.mergedFlags.includes("sign_consent");
  }

  hasSpaceCollab(): boolean {
    return this.mergedFlags && this.mergedFlags.includes("space_collab");
  }

  hidesOtherSpaceAuthors(): boolean {
    return (
      this.mergedFlags &&
      this.mergedFlags.includes("disable_other_space_authors")
    );
  }

  hasDocumentTracking(): boolean {
    return this.mergedFlags && this.mergedFlags.includes("document_tracking");
  }

  hasBetaAccess(): boolean {
    return this.mergedFlags && this.mergedFlags.includes("beta_access");
  }

  hasPromomatsImport(): boolean {
    return this.mergedFlags && this.mergedFlags.includes("promomats_import");
  }

  hasAppConsentBanner(): boolean {
    return this.mergedFlags && this.mergedFlags.includes("app_consent");
  }

  hasDocumentUploadConfirmation(): boolean {
    return (
      this.mergedFlags &&
      this.mergedFlags.includes("document_upload_confirmation")
    );
  }

  hasRequiredCRMContactForSharing(): boolean {
    return (
      this.mergedFlags &&
      this.mergedFlags.includes("sharing_requires_crm_contact")
    );
  }

  hasRequiredCRMConsentForSharing(): boolean {
    return (
      this.mergedFlags &&
      this.mergedFlags.includes("sharing_requires_crm_consent")
    );
  }

  hasRequiredManualConsentForSharing(): boolean {
    return this.mergedFlags?.includes("sharing_requires_manual_consent") ?? false;
  }

  hasEmailVerificationOption(): boolean {
    return (
      this.mergedFlags &&
      this.mergedFlags.includes("sharedspace_email_verification")
    );
  }

  hasTemplateEmailVerification(): boolean {
    return (
      this.mergedFlags &&
      this.mergedFlags.includes("template_email_verification")
    );
  }

  canConfigureHcpUploads(): boolean {
    return this.mergedFlags.includes("enable_hcp_upload_configuration");
  }

  hasUnbrandedDocumentClassification(): boolean {
    return (
      this.mergedFlags?.includes("unbranded_document_classification") ?? false
    );
  }

  hasCodeCard(): boolean {
    return this.mergedFlags && this.mergedFlags.includes("code_card");
  }

  hasAdvancedClipboard(): boolean {
    return this.mergedFlags && this.mergedFlags.includes("advanced_clipboard");
  }

  forcesPitchviewPrefixForDocLinks(): boolean {
    return (
      this.mergedFlags &&
      this.mergedFlags.includes("direct_doc_links_always_with_pitchview_prefix")
    );
  }
}
